// @mui
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
//
import { FIRST_FILING_DISCOUNT_PERCENT, unitPriceCents } from 'src/models';

// ----------------------------------------------------------------------

type Props = {
  truckCount: number;
  // Backend-computed final price; below list price means the first-filing
  // discount was applied.
  priceCents: number;
};

function dollars(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

export default function PriceSummary({ truckCount, priceCents }: Props) {
  const unit = unitPriceCents(truckCount);
  const listTotal = truckCount * unit;
  const discounted = priceCents > 0 && priceCents < listTotal;

  return (
    <Card variant="outlined" sx={{ p: 3 }} data-testid="price-summary">
      <Stack spacing={1.5}>
        <Typography variant="subtitle1">
          IFTA filing for {truckCount} truck{truckCount === 1 ? '' : 's'}
        </Typography>

        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Typography variant="h6">{dollars(unit)}/truck</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            × {truckCount} truck{truckCount === 1 ? '' : 's'}
          </Typography>
          {discounted && (
            <Chip
              size="small"
              color="primary"
              label={`${FIRST_FILING_DISCOUNT_PERCENT}% first filing discount`}
            />
          )}
        </Stack>

        <Divider />

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2">Total</Typography>
          <Stack direction="row" alignItems="baseline" spacing={1.5}>
            {discounted && (
              <Typography
                variant="body1"
                sx={{ textDecoration: 'line-through', color: 'text.disabled' }}
              >
                {dollars(listTotal)}
              </Typography>
            )}
            <Typography variant="h4" data-testid="price-total">
              {dollars(priceCents || listTotal)}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
}
